package com.trevorwiebe.timesheet.admin.employeeHours.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.employeeHours.domain.EmployeeHoursSummary
import com.trevorwiebe.timesheet.admin.employeeHours.domain.canExportPreviousPeriod
import com.trevorwiebe.timesheet.admin.employeeHours.domain.usecases.GetEmployeeHoursReport
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.punch.domain.PunchRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class EmployeeHoursViewModel(
    private val getEmployeeHoursReport: GetEmployeeHoursReport,
    private val punchRepository: PunchRepository,
) : DisposableViewModel() {

    private val _state = MutableStateFlow(EmployeeHoursState())
    val state = _state.asStateFlow()

    /**
     * (Re)loads the report. Called when the screen appears/resumes so that timesheets confirmed
     * elsewhere (the punch detail, an employee submitting) are reflected without an app restart.
     */
    fun refresh() = loadReport()

    @Suppress("UNCHECKED_CAST")
    private fun loadReport() {
        viewModelScope.launch {
            // First load shows the full-screen spinner; a refresh with data already on screen shows
            // a thin top progress bar instead so the existing rows stay visible.
            _state.update {
                it.copy(
                    isLoading = it.summaries.isEmpty(),
                    isRefreshing = it.summaries.isNotEmpty(),
                    error = null,
                )
            }
            val result = getEmployeeHoursReport()
            if (result.error.isNullOrEmpty()) {
                val summaries = result.data as List<EmployeeHoursSummary>
                val rates = punchRepository.getRates().data as? List<Rate> ?: emptyList()
                _state.update {
                    it.copy(
                        isLoading = false,
                        isRefreshing = false,
                        summaries = summaries,
                        rates = rates,
                        canExportPrevious = summaries.canExportPreviousPeriod(),
                    )
                }
            } else {
                _state.update { it.copy(isLoading = false, isRefreshing = false, error = result.error) }
            }
        }
    }

    /**
     * Marks every exported previous-period timesheet as submitted (locking employees out), then
     * reloads so the chips flip to "Pay Period Closed" and export becomes unavailable. Re-checks
     * the gate defensively and no-ops if it isn't satisfied.
     */
    fun markPreviousPeriodSubmitted() {
        val summaries = _state.value.summaries
        if (!summaries.canExportPreviousPeriod()) return

        val entries = summaries.mapNotNull { summary ->
            summary.previousTimeSheet?.let { summary.employee.id to it.id }
        }
        if (entries.isEmpty()) return

        viewModelScope.launch {
            val result = punchRepository.markTimeSheetsSubmitted(entries)
            if (result.error.isNullOrEmpty()) {
                loadReport()
            } else {
                _state.update { it.copy(error = result.error) }
            }
        }
    }
}
